import { formatDateForDisplay } from './date-utils';
import { formatCurrency } from './format-utils';
import { downloadInvoice } from './pdf-generator';

/**
 * Service for handling invoice emails
 */
export const emailService = {
  /**
   * Generate a PDF for an invoice and open the system email client
   * 
   * @param invoice The invoice to send
   * @param recipientEmail Optional recipient email address
   * @param userName Optional user name for the PDF
   * @returns Promise resolving when the system email client is opened
   */
  async openEmailWithInvoice(
    invoice: any,
    recipientEmail?: string,
    userName?: string
  ): Promise<void> {
    const invoiceNumber = invoice.invoiceNumber || invoice.invoice_number || 'N/A';
    console.log(`Preparing email for invoice ${invoiceNumber}`);
    
    try {
      // First, download the PDF so it's ready for the user to attach
      console.log('Downloading PDF for email attachment...');
      await downloadInvoice(invoice, userName);
      
      // Give the download a moment to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Create email content with clear instructions
      const clientName = invoice.clientName || invoice.client_name || 'Client';
      const totalAmount = invoice.totalAmount || invoice.amount || invoice.total_amount || 0;
      const issueDate = invoice.issueDate || invoice.issue_date;
      const dueDate = invoice.dueDate || invoice.due_date;
      
      const subject = this.generateDefaultSubject(invoiceNumber);
      const body = this.generateEmailBodyWithInstructions({
        invoiceNumber,
        clientName,
        totalAmount,
        issueDate,
        dueDate
      });
      
      // Create mailto URL with the email content
      const recipient = recipientEmail || '';
      const encodedSubject = encodeURIComponent(subject);
      const encodedBody = encodeURIComponent(body);
      
      // Open the system email client
      const mailtoUrl = `mailto:${recipient}?subject=${encodedSubject}&body=${encodedBody}`;
      
      console.log('Opening email client...');
      window.open(mailtoUrl, '_blank');
      
      console.log('Email client opened successfully');
      return;
    } catch (error) {
      console.error('Error in openEmailWithInvoice:', error);
      throw new Error(`Failed to prepare invoice email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Generate a default email subject for an invoice
   * 
   * @param invoiceNumber The invoice number
   * @returns A formatted subject line
   */
  generateDefaultSubject(invoiceNumber: string): string {
    return `Invoice #${invoiceNumber}`;
  },

  /**
   * Generate an email message with clear attachment instructions
   * 
   * @param invoiceData The invoice data
   * @returns A formatted message with attachment instructions
   */
  generateEmailBodyWithInstructions(invoiceData: {
    invoiceNumber: string;
    clientName: string;
    totalAmount: number;
    issueDate: string;
    dueDate: string;
  }): string {
    const { invoiceNumber, clientName, totalAmount, issueDate, dueDate } = invoiceData;
    
    // Create the expected filename format (matching the PDF generator)
    const sanitizedInvoiceNumber = invoiceNumber.replace(/[^a-zA-Z0-9-_]/g, '_');
    const sanitizedClientName = clientName.replace(/[^a-zA-Z0-9-_]/g, '_').substring(0, 20);
    const expectedFilename = `Invoice_${sanitizedInvoiceNumber}_${sanitizedClientName}.pdf`;
    
    return `Dear ${clientName},

Please find attached invoice #${invoiceNumber} for services rendered.

Invoice Details:
- Issue Date: ${formatDateForDisplay(issueDate)}
- Due Date: ${formatDateForDisplay(dueDate)}
- Total Amount: ${formatCurrency(Number(totalAmount))}

📎 ATTACHMENT INSTRUCTIONS:
The invoice PDF has been downloaded to your computer (check your Downloads folder).
Please attach the file "${expectedFilename}" to this email before sending.

Payment terms: Net 30 days from invoice date.

Please let us know if you have any questions.

Thank you for your business.

Best regards`;
  },

  /**
   * Generate a default email message for an invoice (legacy method)
   * 
   * @param invoiceData The invoice data
   * @returns A formatted message
   */
  generateDefaultMessage(invoiceData: {
    invoiceNumber: string;
    clientName: string;
    totalAmount: number;
    issueDate: string;
    dueDate: string;
  }): string {
    return this.generateEmailBodyWithInstructions(invoiceData);
  }
};
